import React, { useState } from 'react';
import { NockchainGrpcService, GrpcError } from '../services/grpc';

const WalletService: React.FC = () => {
  const [balanceAddress, setBalanceAddress] = useState('');
  const [balanceSelector, setBalanceSelector] = useState<'address' | 'first_name'>('address');
  const [txId, setTxId] = useState('');
  const [rawTx, setRawTx] = useState('');
  const [response, setResponse] = useState<any>(null);
  const [error, setError] = useState<string>('');
  const [loading, setLoading] = useState(false);

  const isBase58 = (s: string) =>
    /^[123456789ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz]+$/.test(s);

  const handleGetBalance = async () => {
    if (!balanceAddress.trim()) {
      setError(balanceSelector === 'address' ? 'Please enter an address (base58 schnorr pubkey)' : 'Please enter a first-name (base58 hash)');
      return;
    }

    const input = balanceAddress.trim();
    if (!isBase58(input)) {
      setError(
        balanceSelector === 'address'
          ? 'Invalid address format: must be base58 (no 0/O/I/l). This API expects a base58 schnorr pubkey (cheetah), not a pubkey hash / tx id.'
          : 'Invalid first-name format: must be base58 (no 0/O/I/l).'
      );
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const response =
        balanceSelector === 'address'
          ? await NockchainGrpcService.walletGetBalance({ address: input })
          : await NockchainGrpcService.walletGetBalance({ firstName: input });
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleSendTransaction = async () => {
    if (!txId.trim() || !rawTx.trim()) {
      setError('Please enter transaction ID and raw transaction');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const response = await NockchainGrpcService.walletSendTransaction({
        txId: txId.trim(),
        rawTx: rawTx.trim()
      });
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const handleCheckTransactionAccepted = async () => {
    if (!txId.trim()) {
      setError('Please enter a transaction ID');
      return;
    }

    setLoading(true);
    setError('');
    setResponse(null);

    try {
      const response = await NockchainGrpcService.transactionAccepted({
        txId: txId.trim()
      });
      setResponse(response);
    } catch (err) {
      if (err instanceof GrpcError) {
        setError(`gRPC Error: ${err.message}`);
      } else {
        setError(`Error: ${err}`);
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <div className="service-section">
        <h2>Wallet Service</h2>
        <p>Get wallet balances, send transactions, and check transaction status.</p>

        {/* Get Balance */}
        <div className="request-form">
          <h3>Get Wallet Balance</h3>
          <div className="form-group">
            <label htmlFor="balanceSelector">Selector</label>
            <select
              id="balanceSelector"
              value={balanceSelector}
              onChange={(e) => setBalanceSelector(e.target.value as 'address' | 'first_name')}
            >
              <option value="address">Address (base58 schnorr pubkey)</option>
              <option value="first_name">First-name (base58 hash)</option>
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="balanceAddress">
              {balanceSelector === 'address'
                ? 'Wallet Address (base58 schnorr pubkey / cheetah)'
                : 'First-name (base58 hash)'}
            </label>
            <input
              id="balanceAddress"
              type="text"
              value={balanceAddress}
              onChange={(e) => setBalanceAddress(e.target.value)}
              placeholder={
                balanceSelector === 'address'
                  ? 'Paste base58 schnorr pubkey (same value used with nockchain-wallet list-notes-by-address)'
                  : 'Paste base58 first-name hash'
              }
            />
            {balanceSelector === 'address' && (
              <p style={{ marginTop: '0.5rem', color: '#666', fontSize: '0.9rem' }}>
                Tip: this must be a <strong>base58 schnorr pubkey</strong>. If you paste a tx id / pubkey hash, the server will return “Address is improperly formatted”.
              </p>
            )}
          </div>
          <button
            className="btn"
            onClick={handleGetBalance}
            disabled={loading}
          >
            Get Balance
          </button>
        </div>

        {/* Send Transaction */}
        <div className="request-form">
          <h3>Send Transaction</h3>
          <div className="form-group">
            <label htmlFor="sendTxId">Transaction ID</label>
            <input
              id="sendTxId"
              type="text"
              value={txId}
              onChange={(e) => setTxId(e.target.value)}
              placeholder="Enter transaction ID..."
            />
          </div>
          <div className="form-group">
            <label htmlFor="rawTx">Raw Transaction (JSON)</label>
            <textarea
              id="rawTx"
              value={rawTx}
              onChange={(e) => setRawTx(e.target.value)}
              placeholder="Enter raw transaction JSON..."
            />
          </div>
          <button
            className="btn"
            onClick={handleSendTransaction}
            disabled={loading}
          >
            Send Transaction
          </button>
        </div>

        {/* Check Transaction Accepted */}
        <div className="request-form">
          <h3>Check Transaction Accepted</h3>
          <div className="form-group">
            <label htmlFor="checkTxId">Transaction ID</label>
            <input
              id="checkTxId"
              type="text"
              value={txId}
              onChange={(e) => setTxId(e.target.value)}
              placeholder="Enter transaction ID to check..."
            />
          </div>
          <button
            className="btn"
            onClick={handleCheckTransactionAccepted}
            disabled={loading}
          >
            Check Status
          </button>
        </div>
      </div>

      {/* Response Section */}
      {loading && <div className="loading">Loading...</div>}

      {error && (
        <div className="response-section error-response">
          <h3>Error</h3>
          <pre>{error}</pre>
        </div>
      )}

      {response && (
        <div className="response-section">
          <h3>Response</h3>
          <pre className="json-response">{JSON.stringify(response, null, 2)}</pre>
        </div>
      )}
    </div>
  );
};

export default WalletService;
